import { getFontsManager } from 'piivo-poster-engine/src/lib/services/fontsManager';

import { base64EncodeUnicode } from '../../../utils/string';
import { EditableTemplate } from '../types/templates';

const MAX_THUMBNAIL_SIZE = 320;

// Image preloading
const xlinkNS = 'http://www.w3.org/1999/xlink';

/**
 * Gets the dataURL of image and sets it as its 'href'
 * under the xlinkNS namespace
 *
 * @param image - the image to convert
 * @returns the dataURL
 */
export function toDataURL(image: Element): Promise<void> {
  return new Promise(resolve => {
    const img = new Image();
    // CORS workaround, this won't work in IE<11
    // If you are sure you don't need it, remove the next line and the double onerror handler
    // First try with crossorigin set, it should fire an error if not needed
    img.crossOrigin = 'anonymous';

    img.onload = function() {
      // We should now be able to draw it without tainting the canvas
      const canvas = document.createElement('canvas');
      canvas.width = (this as HTMLImageElement).width;
      canvas.height = (this as HTMLImageElement).height;
      // Draw the loaded image
      canvas.getContext('2d')!.drawImage(this as HTMLImageElement, 0, 0);
      // Set our <image>'s href attribute to the dataURL of our canvas
      image.setAttributeNS(xlinkNS, 'href', canvas.toDataURL());
      resolve();
    };

    // No CORS set in the response
    img.onerror = function() {
      // Save the src
      const oldSrc = this.src;
      // There is an other problem
      this.onerror = function() {
        console.warn('failed to load an image at : ', this.src);
        resolve();
      };
      // Remove the crossorigin attribute
      this.removeAttribute('crossorigin');
      // Retry
      this.src = '';
      this.src = oldSrc;
      resolve();
    };

    // Load our external image into our img
    const href = image.getAttributeNS(xlinkNS, 'href')!;
    // Really weird bug : we need to force a no-cached request for the crossOrigin be applied
    img.src = href + (href.includes('?') ? +'&1' : '?1');
  });
}

/**
 * Retrieves the image content, gets it as a dataURL
 * and updates the image element's 'href' under the xlinkNS
 * namespace
 *
 * @param url - the url of the image
 * @param element - the image element to update
 */
export async function createSvgDataURL(
  url: string,
  element: SVGImageElement
): Promise<string | void> {
  return new Promise<string | void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = function() {
      if (this.status === 200) {
        const response = this.responseText || this.response;
        // Extract the pure svg element
        // eslint-disable-next-line no-useless-escape
        const execRes = /\<svg(?:.|\s)*\<\/svg\>/.exec(response);
        if (execRes && execRes[0]) {
          const svg = execRes[0];
          // Create a data URL with the svg as the value
          const dataUrl = 'data:image/svg+xml; charset=utf8, ' + encodeURIComponent(svg);
          element.setAttributeNS(xlinkNS, 'href', dataUrl);
          resolve();
        } else {
          reject(new Error('Could not extract svg from response'));
        }
      } else {
        reject(new Error('Request to svg was not 200'));
      }
    };
    xhr.onerror = function(err) {
      reject(err);
    };
    xhr.open('GET', url);
    xhr.send();
  }).catch(() => toDataURL(element));
}

/**
 * Render template thumbnail (transform SVG into png image).
 * @param origSvgNode - SVG node
 * @param template - Template
 * @param thumbnailScale - Template scale
 * @returns the image (png) data URL
 */
export function renderThumbnail(
  origSvgNode: Element,
  template: EditableTemplate,
  thumbnailScale: number
): Promise<string> {
  // Clone the node, so any changes we make to the images won't
  // be 'saved', in case we need to use the node again
  // Ex: edit a signage twice, successively
  const svgNode = origSvgNode.cloneNode(true) as Element;

  let scale = thumbnailScale || 1;

  if (template.pages[0].width > template.pages[0].height) {
    scale = MAX_THUMBNAIL_SIZE / (template.pages[0].width || 1);
  } else {
    scale = MAX_THUMBNAIL_SIZE / (template.pages[0].height || 1);
  }

  // Thumbnail canvas
  const thumbnailCanvas = document.createElement('canvas');
  thumbnailCanvas.width = template.pages[0].width * scale;
  thumbnailCanvas.height = template.pages[0].height * scale;
  const context = thumbnailCanvas.getContext('2d')!;

  // Loop through all <images> elements of the svg
  const preloadImagesPromises: Promise<unknown>[] = [];
  const images = svgNode.querySelectorAll('image');
  for (const img of images) {
    const href = img.getAttributeNS(xlinkNS, 'href')!;

    const isImageVisible = window.getComputedStyle(img).display !== 'none';
    const isImageExternal = !href.includes('data:image');

    // Check if the image is external
    if (isImageVisible && isImageExternal) {
      if (href.indexOf('.svg') > 0) {
        // If it points to another svg element
        preloadImagesPromises.push(createSvgDataURL(href, img));
      } else {
        // A pixel image
        preloadImagesPromises.push(toDataURL(img));
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return new Promise(async resolve => {
    await Promise.all(preloadImagesPromises);

    // Serialize svg node
    let svg = new XMLSerializer().serializeToString(svgNode);

    // Add thumbnail scale on first <g>
    const actualScale = scale < 1 ? scale : 1;
    svg = svg.replace(/<g/, `<g transform="scale(${actualScale} ${actualScale})"`);

    // Font loading
    let fontsFamilies = '';
    if (template.fonts) {
      for (const font of template.fonts) {
        const cachedFont = getFontsManager().getFromCache(font.name);
        if (cachedFont != null) {
          fontsFamilies += `@font-face { font-family: ${font.name}; src: url('${cachedFont.base64}'); }`;
        }
      }
    }

    const fonts = `<defs><style>${fontsFamilies}</style></defs>`;
    svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:svg="http://www.w3.org/2000/svg" style="background-color: #ffffff;" width="${thumbnailCanvas.width}" height="${thumbnailCanvas.height}">${fonts}${svg}</svg>`;

    // Draw image with the svg and build thumbnail
    const image = new Image();
    image.src = 'data:image/svg+xml;base64,' + base64EncodeUnicode(svg);
    image.onload = function() {
      if (scale <= 1) {
        context.drawImage(image, 0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
      }
      context.scale(scale, scale);
      if (scale > 1) {
        context.drawImage(image, 0, 0);
      }
      resolve(thumbnailCanvas.toDataURL('image/png'));
    };
  });
}
